import React, { useState } from 'react';
import { Bolt, Clock, EyeOff, Info, Loader2, RotateCcw, Save, Send, Undo2 } from 'lucide-react';
import type {
  AdditionalRecipient,
  EncompassRoleOption,
  NotificationPreview,
  NotificationSetting,
  SendCountSummary
} from '@/services/notificationSettingsService';
import type { NotificationRecipientContractEntry } from '../../../../supabase/functions/_shared/notification-recipient-contract';
import {
  isRecipientConfigV1,
  type RecipientConfigV1,
  type TemplateOverrides
} from './types';
import { ActivityTab } from './ActivityTab';
import { AdvancedTab } from './AdvancedTab';
import { EmailPreviewPane } from './EmailPreviewPane';
import { previewBodyText } from './previewBodyText';
import { RecipientZones } from './RecipientZones';
import { SubjectBodyEditor } from './SubjectBodyEditor';
import {
  ALWAYS_ON_NOTIFICATION_TYPES,
  canEditRecipients,
  canEditTemplate,
  codeOwnedRecipientConfigFor,
  effectiveRecipientConfigFor,
  recipientContractFor,
  SEND_TEST_DISABLED_TYPES
} from './config';

interface DetailPaneProps {
  setting: NotificationSetting | null;
  draftConfig: RecipientConfigV1 | null;
  draftOverrides: TemplateOverrides;
  availableUsers: AdditionalRecipient[];
  userRoles: string[];
  encompassRoles: EncompassRoleOption[];
  encompassRolesError?: string | null;
  countSummary?: SendCountSummary;
  countsError?: string | null;
  dirty: boolean;
  saving: boolean;
  sendingTest: boolean;
  preview: NotificationPreview | null;
  previewLoading: boolean;
  previewError: string | null;
  showPreview: boolean;
  onToggleEnabled: (enabled: boolean) => void;
  onTogglePreview: () => void;
  onConfigChange: (config: RecipientConfigV1) => void;
  onResetConfig: () => void;
  onUndoConfigReset: () => void;
  onOverridesChange: (overrides: TemplateOverrides) => void;
  onSave: () => void;
  onTestSend: () => void;
}

function triggerTag(setting: NotificationSetting): string {
  const category = setting.category?.replace(/[_-]/g, ' ') || 'Automatic';
  if (category.toLowerCase().includes('appraisal')) {
    return 'Document Upload';
  }
  if (category.toLowerCase().includes('compensation')) {
    return 'Status Change';
  }
  if (category.toLowerCase().includes('disclosure')) {
    return 'Field Populated';
  }
  return category;
}

function relativeTimeLabel(value: string | null | undefined): string {
  if (!value) {
    return 'No sends found';
  }
  const sent = new Date(value);
  if (Number.isNaN(sent.getTime())) {
    return value;
  }
  const now = new Date();
  const diffMs = now.getTime() - sent.getTime();
  const diffMinutes = Math.max(0, Math.floor(diffMs / 60_000));
  if (diffMinutes < 1) {
    return 'Just now';
  }
  if (diffMinutes < 60) {
    return `${diffMinutes}m ago`;
  }
  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) {
    return `${diffHours}h ago`;
  }
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays === 1) {
    return 'Yesterday';
  }
  if (diffDays < 7) {
    return `${diffDays}d ago`;
  }
  return sent.toLocaleDateString();
}

function recipientSummaryZones(recipients: string): { to: string[]; cc: string[]; additional: string[] } {
  const zones = { to: [] as string[], cc: [] as string[], additional: [] as string[] };
  const parts = recipients.split(/[,;]/).map((part) => part.trim()).filter(Boolean);

  for (const part of parts) {
    const normalized = part.replace(/\((to|cc)\)/gi, '').trim();
    if (/\(cc\)/i.test(part) || /\bcc\b/i.test(part)) {
      zones.cc.push(normalized);
    } else if (/\(to\)/i.test(part) || /\bborrower/i.test(part) || zones.to.length === 0) {
      zones.to.push(normalized);
    } else {
      zones.cc.push(normalized);
    }
  }

  return zones;
}

function recipientStats(
  setting: NotificationSetting,
  config: RecipientConfigV1 | null,
  contract: NotificationRecipientContractEntry | null,
  usingCodeDefaults: boolean
): { count: string; sub: string } {
  if (contract) {
    const automatic = contract.lockedTo.length + contract.lockedCc.length;
    const configured = config ? config.to.length + config.cc.length : 0;
    if (
      usingCodeDefaults
      && contract.primaryMode === 'code_default_until_overridden'
    ) {
      return { count: 'Default', sub: 'code-owned until customized' };
    }
    if (automatic > 0) {
      return {
        count: 'Automatic',
        sub: `${automatic} required route${automatic === 1 ? '' : 's'} · ${configured} configured addition${configured === 1 ? '' : 's'}`
      };
    }
  }
  if (config) {
    return {
      count: String(config.to.length + config.cc.length),
      sub: `${config.to.length} TO · ${config.cc.length} CC`
    };
  }

  const zones = recipientSummaryZones(setting.recipients);
  const total = zones.to.length + zones.cc.length + zones.additional.length;
  if (total === 0) {
    return { count: 'Default', sub: 'code-owned routing' };
  }
  const additional = zones.additional.length > 0 ? ` · ${zones.additional.length} additional` : '';
  return {
    count: String(total),
    sub: `${zones.to.length} TO · ${zones.cc.length} CC${additional}`
  };
}

function ReadOnlyRecipientZones({ recipients }: { recipients: string }) {
  const zones = recipientSummaryZones(recipients);
  const renderZone = (label: string, values: string[]) => (
    <div className="en-zone">
      <div className="en-zone-head">
        <div className="en-zone-label">{label}</div>
      </div>
      <div className="en-chips">
        {values.length === 0 ? (
          <span className="en-empty-zone">No recipients</span>
        ) : values.map((value) => (
          <span key={`${label}-${value}`} className="en-role-chip">
            <span className="en-role-dot" />
            {value}
          </span>
        ))}
      </div>
    </div>
  );

  return (
    <div className="en-recipient-zones">
      {renderZone('To', zones.to)}
      {renderZone('CC', zones.cc)}
      {renderZone('Additional individuals', zones.additional)}
    </div>
  );
}

export function DetailPane({
  setting,
  draftConfig,
  draftOverrides,
  availableUsers,
  userRoles,
  encompassRoles,
  encompassRolesError,
  countSummary,
  countsError,
  dirty,
  saving,
  sendingTest,
  preview,
  previewLoading,
  previewError,
  showPreview,
  onToggleEnabled,
  onTogglePreview,
  onConfigChange,
  onResetConfig,
  onUndoConfigReset,
  onOverridesChange,
  onSave,
  onTestSend
}: DetailPaneProps) {
  const [tab, setTab] = useState<'configuration' | 'activity' | 'advanced'>('configuration');

  if (!setting) {
    return (
      <main className="en-detail">
        <div className="en-detail-empty">
          Select a notification to configure.
        </div>
      </main>
    );
  }

  const recipientEditable = canEditRecipients(setting.notification_type);
  const recipientContract = recipientContractFor(setting.notification_type);
  const templateEditable = canEditTemplate(setting.notification_type);
  const testDisabled = SEND_TEST_DISABLED_TYPES.has(setting.notification_type);
  const hasStoredRecipientConfig = isRecipientConfigV1(
    setting.recipient_config
  );
  const resetPending = recipientEditable
    && draftConfig === null
    && setting.recipient_config !== null;
  const initialRecipientConfig = recipientEditable
    ? effectiveRecipientConfigFor(setting)
    : null;
  const draftStillMatchesInitialRoute = JSON.stringify(draftConfig)
    === JSON.stringify(initialRecipientConfig);
  const displayedRecipientConfig = recipientEditable
    ? draftConfig
      ?? (
        resetPending
          ? codeOwnedRecipientConfigFor(setting)
          : effectiveRecipientConfigFor(setting, null)
      )
    : { version: 1 as const, to: [], cc: [] };
  const usingCodeDefaults = resetPending || (
    !hasStoredRecipientConfig
    && draftStillMatchesInitialRoute
  );
  const visibleRecipientStats = recipientStats(
    setting,
    displayedRecipientConfig,
    recipientContract,
    usingCodeDefaults
  );
  const defaultBody = previewBodyText(preview?.html);
  const previewVisible = showPreview && tab === 'configuration';
  const recipientRoutingHint = recipientContract?.routingMode === 'settings_primary'
    ? 'Configured routing'
    : recipientContract?.routingMode === 'required_plus_additions'
      ? 'Required routing plus additions'
      : recipientContract?.routingMode === 'display_only'
        ? 'Code-owned routing'
        : resetPending
          ? 'Resetting to code defaults'
          : usingCodeDefaults
            ? 'Code defaults until customized'
            : !hasStoredRecipientConfig
              ? 'Exact draft routing'
            : 'Exact saved routing';
  const routingNotice = recipientContract?.routingMode === 'required_plus_additions'
    ? 'Automatic recipients are code-owned and cannot be removed. The editable boxes add recipients to that required route.'
    : recipientContract?.routingMode === 'settings_primary'
      ? recipientContract.lockedTo.length > 0 || recipientContract.lockedCc.length > 0
        ? 'Automatic recipients remain code-owned. The saved To and CC boxes define the configurable audience. Loan roles are available only when one triggering loan exists.'
        : 'The saved To and CC boxes define this notification’s configured audience. Loan roles are available only when one triggering loan exists.'
      : recipientContract?.routingMode === 'authoritative'
        ? resetPending
          ? 'The code-default route is shown. Save to remove the exact override.'
          : usingCodeDefaults
            ? 'This route starts from code defaults. Saving a recipient change makes the displayed To and CC lists authoritative.'
            : !hasStoredRecipientConfig
              ? 'This draft replaces the code defaults when saved. Only the displayed To and CC lists will be used.'
            : 'This saved routing is authoritative. The sender will not re-add loan-team roles omitted here.'
        : 'Recipients are resolved by the sender and shown here for visibility.';

  return (
    <main className="en-detail">
      <div className="en-detail-tabs-root">
        <div className="en-detail-head">
          <div className="en-detail-eyebrow">{setting.category} · {triggerTag(setting)}</div>
          <div className="en-detail-title-row">
            <div className="min-w-0 flex-1">
              <h1 className="en-detail-title">{setting.name}</h1>
              <div className="en-detail-desc">{setting.description}</div>
            </div>
            <div className="en-detail-actions">
              {ALWAYS_ON_NOTIFICATION_TYPES.has(setting.notification_type) ? (
                <span className="en-status-pill is-success">Always on</span>
              ) : (
                <button
                  type="button"
                  role="switch"
                  aria-checked={setting.is_enabled}
                  aria-label={setting.is_enabled ? 'Disable notification' : 'Enable notification'}
                  className={`en-toggle ${setting.is_enabled ? 'is-on' : ''}`}
                  onClick={() => onToggleEnabled(!setting.is_enabled)}
                />
              )}
              <button type="button" className="en-btn" onClick={onTogglePreview}>
                <EyeOff />
                {showPreview ? 'Hide preview' : 'Show preview'}
              </button>
              <button
                type="button"
                disabled={testDisabled || sendingTest}
                data-preserve-disabled
                className="en-btn"
                onClick={onTestSend}
                title={testDisabled ? 'This notification uses a dedicated sender; generic test send is disabled.' : undefined}
              >
                {sendingTest ? <Loader2 className="animate-spin" /> : <Send />}
                Send test
              </button>
              <button type="button" className="en-btn en-btn-primary" data-preserve-disabled disabled={!dirty || saving} onClick={onSave}>
                {saving ? <Loader2 className="animate-spin" /> : <Save />}
                Save
              </button>
            </div>
          </div>
          <div className="en-tabs">
            <button
              type="button"
              className={`en-tab ${tab === 'configuration' ? 'is-active' : ''}`}
              onClick={() => setTab('configuration')}
            >
              Configuration
            </button>
            <button
              type="button"
              className={`en-tab ${tab === 'activity' ? 'is-active' : ''}`}
              onClick={() => setTab('activity')}
            >
              Activity ({countsError ? 0 : countSummary?.count ?? 0})
            </button>
            <button
              type="button"
              className={`en-tab ${tab === 'advanced' ? 'is-active' : ''}`}
              onClick={() => setTab('advanced')}
            >
              Advanced
            </button>
          </div>
        </div>
        <div className={`en-detail-body ${previewVisible ? '' : 'no-preview'}`}>
          <div className="en-config-pane">

          {tab === 'configuration' && (
          <>
            <section className="en-section">
              <div className="en-section-title">Trigger</div>
              <div className="en-trigger-card">
                <div className="en-trigger-icon"><Bolt /></div>
                <div className="min-w-0 flex-1">
                  <div className="en-trigger-meta">{triggerTag(setting)}</div>
                  <div className="en-trigger-text">{setting.trigger_description}</div>
                  <span className="en-trigger-cadence"><Clock className="h-3 w-3" /> {setting.notification_type === 'artemis_operational_health' ? 'Every 15 minutes' : setting.notification_type === 'marketing_market_brief_completed' ? 'On completion · checked every 5 minutes' : ['marketing_request_lo_reminder', 'marketing_request_director_digest'].includes(setting.notification_type) ? 'Daily · around 9 AM Pacific' : ['marketing_request_lo_update', 'marketing_request_director_alert', 'calendar_booking_update'].includes(setting.notification_type) ? 'Immediate · checked every 5 minutes' : 'Immediate'}</span>
                </div>
              </div>
              <div className="en-stats">
                <div className="en-stat-card">
                  <div className="en-stat-label">Sent (30d)</div>
                  <div className="en-stat-value">{countsError ? '-' : countSummary?.count ?? 0}</div>
                  <div className="en-stat-sub">{countsError ? 'unavailable' : `avg ${Math.round(((countSummary?.count ?? 0) / 30) * 10) / 10}/day`}</div>
                </div>
                <div className="en-stat-card">
                  <div className="en-stat-label">Last sent</div>
                  <div className="en-stat-value small">
                    {countsError ? 'Unavailable' : relativeTimeLabel(countSummary?.lastSent)}
                  </div>
                  <div className="en-stat-sub">{setting.is_enabled ? 'active' : 'paused'}</div>
                </div>
                <div className="en-stat-card">
                  <div className="en-stat-label">Recipients</div>
                  <div className="en-stat-value">{visibleRecipientStats.count}</div>
                  <div className="en-stat-sub">{visibleRecipientStats.sub}</div>
                </div>
              </div>
            </section>

            <section className="en-section">
              <div className="en-section-title">
                <span>Recipients</span>
                <div className="en-recipient-heading-actions">
                  <span className="en-hint">{recipientRoutingHint}</span>
                  {recipientEditable && setting.recipient_config !== null && (
                    <button
                      type="button"
                      className="en-zone-add"
                      onClick={resetPending ? onUndoConfigReset : onResetConfig}
                    >
                      {resetPending ? <Undo2 className="h-3.5 w-3.5" /> : <RotateCcw className="h-3.5 w-3.5" />}
                      {resetPending ? 'Undo reset' : 'Use code defaults'}
                    </button>
                  )}
                </div>
              </div>
              {resetPending && (
                <div className="en-routing-reset-notice" role="status">
                  Recipient overrides will reset to code-owned routing when you save.
                </div>
              )}
              {recipientContract && (
                <div className="en-routing-note">
                  <Info className="h-3.5 w-3.5" aria-hidden="true" />
                  <span>{routingNotice}</span>
                </div>
              )}
              {recipientContract && displayedRecipientConfig ? (
                <RecipientZones
                  config={displayedRecipientConfig}
                  contract={recipientContract}
                  availableUsers={availableUsers}
                  userRoles={userRoles}
                  encompassRoles={encompassRoles}
                  encompassRolesError={encompassRolesError}
                  usingCodeDefaults={usingCodeDefaults}
                  disabled={!recipientEditable}
                  onChange={onConfigChange}
                />
              ) : <ReadOnlyRecipientZones recipients={setting.recipients} />}
            </section>

            <section className="en-section">
              <div className="en-section-title">
                <span>Subject &amp; Body</span>
                <span className="en-template-locked">
                  {templateEditable ? 'Edit template' : 'Code-owned in v1'}
                </span>
              </div>
              <SubjectBodyEditor
                notificationType={setting.notification_type}
                overrides={draftOverrides}
                defaultSubject={preview?.subject}
                defaultBody={defaultBody}
                disabled={!templateEditable}
                onChange={onOverridesChange}
              />
            </section>
          </>
          )}

          {tab === 'activity' && (
            <ActivityTab notificationType={setting.notification_type} />
          )}

          {tab === 'advanced' && (
            <AdvancedTab
              setting={setting}
              countSummary={countSummary}
              countsError={countsError}
            />
          )}
          </div>
          {previewVisible && (
            <EmailPreviewPane
              preview={preview}
              loading={previewLoading}
              error={previewError}
              setting={setting}
              draftConfig={draftConfig}
              availableUsers={availableUsers}
            />
          )}
        </div>
      </div>
    </main>
  );
}
